interface JwtPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  // Index signature must come after all named members
  // eslint-disable-next-line @typescript-eslint/member-ordering
  [key: string]: unknown;
}

/**
 * Decodes a JWT token and returns its payload.
 * Does NOT validate the token signature - only extracts the payload.
 *
 * @param token - The JWT token string
 * @returns The decoded payload object
 * @throws Error if the token format is invalid
 */
export function parseJwtPayload(token: string): JwtPayload {
  const parts = token.split(".");
  if (parts.length !== 3) {
    throw new Error("Invalid JWT format");
  }

  // eslint-disable-next-line prefer-destructuring
  const payload = parts[1];
  // atob is available in browser/React Native environments and is appropriate for JWT decoding
  // eslint-disable-next-line no-restricted-globals
  const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(decoded) as JwtPayload;
}

/**
 * Checks if a JWT token is expired based on its `exp` claim.
 *
 * @param token - The JWT token string
 * @returns true if the token is expired or has no exp claim, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  try {
    const payload = parseJwtPayload(token);
    if (!payload.exp) {
      // No expiration claim - treat as expired for safety
      return true;
    }

    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    return currentTimeInSeconds >= payload.exp;
  } catch {
    // If we can't parse the token, treat it as expired
    return true;
  }
}

/**
 * Checks if a JWT token is within a threshold of expiring.
 * Used for proactive token refresh to avoid edge cases where a token
 * expires between validation and API call.
 *
 * @param token - The JWT token string
 * @param thresholdInSeconds - Number of seconds before expiry to consider "near expiry" (default: 60)
 * @returns true if the token is expired or will expire within the threshold
 */
export function isTokenNearExpiry(token: string, thresholdInSeconds = 60): boolean {
  try {
    const payload = parseJwtPayload(token);
    if (!payload.exp) {
      // No expiration claim - treat as near expiry for safety
      return true;
    }

    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    const expiryThreshold = payload.exp - thresholdInSeconds;
    return currentTimeInSeconds >= expiryThreshold;
  } catch {
    // If we can't parse the token, treat it as near expiry
    return true;
  }
}

/**
 * Gets the remaining time until token expiration in seconds.
 *
 * @param token - The JWT token string
 * @returns Number of seconds until expiration, or -1 if already expired or invalid
 */
export function getTokenExpiryInSeconds(token: string): number {
  try {
    const payload = parseJwtPayload(token);
    if (!payload.exp) {
      return -1;
    }

    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    const remainingTime = payload.exp - currentTimeInSeconds;
    return remainingTime > 0 ? remainingTime : -1;
  } catch {
    return -1;
  }
}
